import { MarkerSeverity } from "../enums";
import { IMarkerTypeInCluster, ISystemData } from "../interfaces";

interface ISlice {
  amount: number;
  percent: number;
  type: MarkerSeverity;
  color: string;
}

const minimumPercentage: number = 0.05;

function clusterIconGenerator(
  markers: any[],
  markerTypesInfo: IMarkerTypeInCluster[],
  circleSizeRadius: number = 60
) {
  // Readying the slice
  const emptySlices: ISlice[] = markerTypesInfo.map(
    (markerTypeInfo: IMarkerTypeInCluster) => {
      return {
        amount: 0,
        percent: 0,
        type: markerTypeInfo.type,
        color: markerTypeInfo.color,
      };
    }
  );
  var numMarkers = markers.length;

  // Adding value if any marker has a type not found in the markerTypes array.
  const defaultSlice: ISlice = {
    amount: 0,
    percent: 0,
    type: MarkerSeverity.Unknown,
    color: "grey",
  };
  emptySlices.push(defaultSlice);

  const typesInCluster: Set<MarkerSeverity> = new Set();

  // Counting amount of each type of marker within the cluster, and update the slices for each type with this amount.
  var slices: ISlice[] = markers
    .reduce((acc: any, marker: ISystemData) => {
      const markerType = marker.status.statusType;
      typesInCluster.add(markerType);

      // Find the slice for the same markertype as the marker
      const indexOfMarkersType = acc.findIndex((slice: any) => {
        return markerType === slice.type;
      });

      // Count up the amount in the slice for the given type, or count up default if no slice with the marker type is found.
      indexOfMarkersType >= 0
        ? (acc[indexOfMarkersType].amount += 1)
        : (acc[acc.length - 1].amount += 1);

      //acc[indexOfMarkersType].percent = acc[indexOfMarkersType].amount / numMarkers;
      return acc;
    }, emptySlices.slice())
    .filter((slice: ISlice) => slice.amount > 0);

  // Find the percentage each slice should take of the total icon, and break if any is below minimum, making the next loop run as true.
  let needToPadForMinimum = false;
  slices.forEach((slice) => {
    slice.percent = slice.amount / numMarkers;
    if (slice.amount > 0 && slice.percent < minimumPercentage) {
      needToPadForMinimum = true;
    }
  });

  // If one of the slices are below the minimum size, all slices in the icon is given the minimum amount to start, and it is subtracted from the total procentage.

  if (needToPadForMinimum) {
    slices.forEach((slice) => {
      if (slice.amount > 0) {
        slice.percent =
          minimumPercentage +
          (slice.amount / numMarkers) *
            (1 - minimumPercentage * typesInCluster.size);
      }
    });
  }

  //Calls the helping method that makes the svg string for the icon
  var newIconURL = makePieChartIcon(slices, circleSizeRadius);

  // Return an object following the type: ClusterIconStyle
  return [
    {
      url: newIconURL,
      height: circleSizeRadius,
      width: circleSizeRadius,
      textColor: "#FFFFFF",
      textSize: 22,
      fontFamily: "Oswald",
      fontStyle: "normal",
      fontWeight: "500",
    },
  ];
}

const makePieChartIcon = (slices: ISlice[], circleSizeRadius: number) => {
  const svgNS = "http://www.w3.org/2000/svg";

  var svg = document.createElementNS(svgNS, "svg");
  svg.setAttribute("viewBox", "-1.1 -1.1 2.2 2.2");
  svg.setAttribute("style", "transform: rotate(-90deg)");
  svg.setAttribute("height", `${circleSizeRadius * 2}`);
  svg.setAttribute("width", `${circleSizeRadius * 2}`);

  var circle = document.createElementNS(svgNS, "circle");
  circle.setAttribute("r", "1.1");
  circle.setAttribute("fill", "white");
  svg.appendChild(circle);

  let cumulativePercent = 0;

  slices.forEach((slice: ISlice) => {
    // destructuring assignment sets the two variables at once
    const [startX, startY] = getCoordinatesForPercent(cumulativePercent);

    // each slice starts where the last slice ended, so keep a cumulative percent
    cumulativePercent += slice.percent;

    const [endX, endY] = getCoordinatesForPercent(cumulativePercent);

    // if the slice is more than 50%, take the large arc (the long way around)
    const largeArcFlag = slice.percent > 0.5 ? 1 : 0;

    // create an array and join it just for code readability
    const pathData = [
      `M ${startX} ${startY}`, // Move
      `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`, // Arc
      `L 0 0`, // Line
    ].join(" ");

    // create a <path> and append it to the <svg> element
    const path = document.createElementNS(svgNS, "path");
    path.setAttribute("d", pathData);
    path.setAttribute("fill", slice.color);
    svg.appendChild(path);
  });

  /*
    var innerCircle = document.createElementNS(svgNS, 'circle')
    innerCircle.setAttribute('r', '0.7')
    innerCircle.setAttribute('fill', 'orange')
    svg.appendChild(innerCircle);
    */

  var svgUrl =
    "data:image/svg+xml;charset=UTF-8," +
    encodeURIComponent(serializeXmlNode(svg));
  return svgUrl;
};

const serializeXmlNode = (xmlNode: any) => {
  if (typeof window.XMLSerializer != "undefined") {
    return new window.XMLSerializer().serializeToString(xmlNode);
  } else if (typeof xmlNode.xml != "undefined") {
    return xmlNode.xml;
  }
  return "";
};

function getCoordinatesForPercent(percent: number) {
  const x = Math.cos(2 * Math.PI * percent);
  const y = Math.sin(2 * Math.PI * percent);
  return [x, y];
}

export default clusterIconGenerator;
